"use client";

import * as React from "react";
import { NavLink } from "react-router-dom"; // Import NavLink
import { ChevronRight, type LucideIcon } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { nodes, NodeType } from "@/config/nodes";

export function NavMain({
  items = [],
  label,
}: {
  label?: string;
  items: {
    name: string;
    url?: string;
    icon?: React.ElementType;
    isActive?: boolean;
    onClick?: () => void;
    type?: NodeType;
    variant?: "default" | "outline" | "primary";
    items?: {
      url: string;
      name: string;
      key?: string;
      icon?: LucideIcon;
      isActive?: boolean;
      onClick?: () => void;
      type: NodeType;
    }[];
  }[];
}): React.ReactNode {
  // Remove useNavigation hook

  return (
    <SidebarGroup>
      {label && (
        <SidebarGroupLabel className="-translate-y-1.25">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu className="gap-0.5">
        {items.map((item) =>
          item?.items?.length ? (
            <Collapsible
              key={item.name}
              asChild
              defaultOpen={item.isActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.name} variant={item.variant}>
                    {item.icon && <item.icon />}
                    <span>{item.name}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub className="gap-2">
                    {item?.items?.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.name}>
                        <SidebarMenuSubButton
                          onClick={subItem?.onClick || (() => {})}
                          asChild
                        >
                          <a
                            href={subItem.url}
                            className={
                              "inline-flex cursor-pointer items-center gap-2 px-3"
                            }
                          >
                            {subItem.icon ? (
                              <subItem.icon className="h-4 w-4" />
                            ) : (
                              nodes[subItem?.type]?.icon
                            )}
                            <span>{subItem.name}</span>
                          </a>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            <SidebarMenuItem key={item.name}>
              {/* Wrap button with NavLink */}
              <NavLink to={item.url || "#"}>
                <SidebarMenuButton
                  tooltip={item.name}
                  onClick={item.onClick || (() => {})}
                  variant={item.variant}
                >
                  {/* Use optional chaining and provide fallback for icon */}
                  {item?.icon ? (
                    <item.icon />
                  ) : (
                    nodes[item?.type as keyof typeof nodes]?.icon
                  )}
                  <span>{item.name}</span>
                </SidebarMenuButton>
              </NavLink>
            </SidebarMenuItem>
          ),
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}
